import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { MetricChart, useMetricChart } from '@/components/charts/generic';
import { OptoColors } from '@/config/settings';
import { parseLocale } from '@/utils/localeFormatters';

import { buildWelfareChartSpec, type WelfareDataset } from '../utils/smoltChartSpecs';

import styles from './WelfareLineChart.module.scss';

/** The palette colours the welfare development lines use. */
export const WELFARE_CHART_COLORS = {
    orange: OptoColors.Orange,
    red: OptoColors.Red,
    riverside: OptoColors.Riverside,
    yellow: OptoColors.Yellow,
} as const;

export type WelfareChartColor = (typeof WELFARE_CHART_COLORS)[keyof typeof WELFARE_CHART_COLORS];

export type WelfareLineChartDataset = WelfareDataset & { color: WelfareChartColor };

interface WelfareLineChartProps {
    datasets: WelfareLineChartDataset[];
    chartHeight?: number;
    className?: string;
    isLoading?: boolean;
    decimals?: number;
}

export default function WelfareLineChart(props: WelfareLineChartProps) {
    const { datasets, className, isLoading = false, chartHeight = 150, decimals = 0 } = props;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    const spec = useMemo(
        () => buildWelfareChartSpec(datasets, locale, decimals),
        [datasets, locale, decimals]
    );
    const chart = useMetricChart(spec);

    return (
        <div className={classNames(styles.chartContainer, className)}>
            <div className={styles.header}>
                <span className={styles.headerLabel}>
                    {t('smoltReport.detailView.developmentTitle')}
                </span>
                <span className={styles.headerPeriod}>
                    {t('smoltReport.detailView.periodTitle')}
                </span>
            </div>
            <MetricChart
                kind="line"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                height={chartHeight}
                isLoading={isLoading}
            />
        </div>
    );
}
